import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import {
  api, ApiError,
  type Kayttaja, type AcademyOmaKurssi, type AcademyOsio,
  type KoeKysymys, type KoeTulos,
} from "./api";
import { Ohje } from "./Ohje";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Academy — oppijan näkymä.

   Kolme tilaa: lista, kurssi, koe. Kurssi luetaan ensin ja koe
   avataan sen jälkeen; koe arvostellaan palvelimella eikä täällä.

   Oikeat vastaukset eivät tule tähän komponenttiin missään
   vaiheessa. Jos ne tulisivat, koe olisi koristelu — ja
   sertifikaatin ensimmäinen ehto sen mukana.
   ═══════════════════════════════════════════════════════════ */

type Tila =
  | { nakyma: "lista" }
  | { nakyma: "kurssi"; id: number }
  | { nakyma: "koe"; id: number };

export default function Academy({ kayttaja }: { kayttaja: Kayttaja }) {
  const [kurssit, setKurssit] = useState<AcademyOmaKurssi[]>([]);
  const [tila, setTila] = useState<Tila>({ nakyma: "lista" });
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);

  const saaHallita = kayttaja.oikeudet.includes("osaaminen_rajat");

  const lataa = useCallback(async () => {
    try {
      setKurssit(await api.academyOmat());
      setVirhe(null);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.kurssit_virhe"));
    } finally { setLadataan(false); }
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  if (ladataan) return <p className="dim">{t("ltar.ladataan")}</p>;

  if (tila.nakyma === "kurssi") {
    return <Kurssi id={tila.id}
                   takaisin={() => setTila({ nakyma: "lista" })}
                   kokeeseen={() => setTila({ nakyma: "koe", id: tila.id })} />;
  }
  if (tila.nakyma === "koe") {
    return <Koe id={tila.id}
                valmis={async () => { await lataa(); setTila({ nakyma: "lista" }); }}
                takaisin={() => setTila({ nakyma: "kurssi", id: tila.id })} />;
  }

  return (
    <>
      {/* Näkymän nimi h1:nä: renderöintiportti tunnistaa näkymän siitä,
          ja ilman sitä Academy oli sille näkymätön. Kolme identtistä
          virhebanneririviä tässä tiedostossa — tämä on ylin, se joka
          on koko näkymän juuressa. */}
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.academy")}</h1></div>
      </header>
      {/* Ohje ylös (Toni 24.9.2026). «Kuka» = /api/academy*: vaadi + moduuli osaaminen; koe vaatii henkilöön
          liitetyn tunnuksen; toisen tilanne osaaminen_arviointi, julkaisu ja luonnokset osaaminen_rajat. */}
      <Ohje
        kuka={<>{t("aca.ohje.kuka")}</>}
        luvut={<>{t("aca.ohje.luvut")}</>} />
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {!kayttaja.resurssi_id && (
        <p className="huom" role="status">
          {t("aca.ei_henkiloa")}
        </p>
      )}

      {saaHallita && <Hallinta onMuutos={lataa} />}

      <section className="panel">
        <div className="panel__head">
          <h2>{t("aca.omat_kurssit")}</h2>
          <span className="dim">
            {t("aca.suoritettu_lkm", { n: kurssit.filter((k) => k.voimassa).length, m: kurssit.length })}
          </span>
        </div>

        {virhe ? (
          /* Tyhjä lista ja epäonnistunut haku eivät saa näyttää samalta:
             "ei kursseja" olisi väärä väite silloin kun kursseja ei
             saatu haettua. Sama vika kuin nolla joka tarkoittaa
             "ei tiedetä". */
          <p className="dim">{t("aca.lista_virhe")}</p>
        ) : kurssit.length === 0 ? (
          <p className="dim">
            {t("aca.ei_julkaistuja")}
          </p>
        ) : (
          <div className="akatemia__ruudukko">
            {kurssit.map((k) => {
              const vanhentunut = !!k.lapaisty && !k.voimassa;
              const luokka = "akatemia__kortti"
                + (k.lukossa ? " akatemia__kortti--lukossa" : "")
                + (k.voimassa ? " akatemia__kortti--valmis" : "")
                + (vanhentunut ? " akatemia__kortti--vanha" : "");
              return (
                <article key={k.id} className={luokka}>
                  <div className="akatemia__yla">
                    <h3 className="akatemia__nimi">{k.nimi}</h3>
                    <span className="akatemia__rata">{k.rata ?? t("aca.yleinen")}</span>
                  </div>

                  {k.kuvaus && <p className="akatemia__kuvaus">{k.kuvaus}</p>}

                  <div>
                    <Tilamerkki k={k} />
                  </div>

                  <div className="akatemia__ala">
                    <span className="akatemia__meta">
                      {t("aca.kortti_meta", { o: k.osioita ?? 0, k: k.kysymyksia ?? 0 })}
                      {k.pisteet !== null && ` · ${k.pisteet} %`}
                      {k.voimassa_asti && ` · ${t("aca.voimassa", { pvm: naytaPvm(k.voimassa_asti) ?? "" })}`}
                    </span>
                    <button className={k.voimassa ? "nappi nappi--kevyt" : "nappi nappi--paa"}
                            disabled={k.lukossa}
                            onClick={() => setTila({ nakyma: "kurssi", id: k.id })}>
                      {k.voimassa ? t("aca.kertaa") : vanhentunut ? t("aca.uusi") : t("aca.avaa")}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

      </section>
    </>
  );
}

function Tilamerkki({ k }: { k: AcademyOmaKurssi }) {
  if (k.lukossa) {
    return (
      <span className="akatemia__merkki" title={k.vaatii ? t("aca.vaatii", { kurssi: k.vaatii }) : undefined}>
        {t("aca.lukossa")}{k.vaatii ? ` · ${k.vaatii}` : ""}
      </span>
    );
  }
  if (k.voimassa) return <span className="akatemia__merkki akatemia__merkki--valmis">{t("aca.suoritettu")}</span>;
  if (k.lapaisty) return <span className="akatemia__merkki akatemia__merkki--vanha">{t("aca.vanhentunut")}</span>;
  if (k.yritys) return <span className="akatemia__merkki">{t("aca.hylatty_yritys", { n: k.yritys })}</span>;
  return <span className="akatemia__merkki">{t("aca.aloittamatta")}</span>;
}

/* ── Kurssin sisältö ───────────────────────────────────────── */

function Kurssi({
  id, takaisin, kokeeseen,
}: { id: number; takaisin: () => void; kokeeseen: () => void }) {
  const [osiot, setOsiot] = useState<AcademyOsio[]>([]);
  const [nimi, setNimi] = useState("");
  const [kysymyksia, setKysymyksia] = useState(0);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ladataan, setLadataan] = useState(true);

  useEffect(() => {
    api.academyKurssi(id)
      .then((r) => { setOsiot(r.osiot); setNimi(r.kurssi.nimi); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.kurssi_virhe")))
      .finally(() => setLadataan(false));
    api.academyKoe(id).then((r) => setKysymyksia(r.kysymykset.length)).catch(() => {});
  }, [id]);

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{nimi || t("aca.kurssi")}</h2>
        <div>
          <button className="nappi nappi--kevyt" onClick={takaisin}>← {t("aca.kursseihin")}</button>{" "}
          <button className="nappi nappi--paa" disabled={kysymyksia === 0} onClick={kokeeseen}>
            {kysymyksia === 0 ? t("aca.ei_koetta") : t("aca.aloita_koe", { n: kysymyksia })}
          </button>
        </div>
      </div>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {ladataan && <p className="dim">{t("ltar.ladataan")}</p>}

      <div className="akatemia__luku">
        {osiot.map((o, i) => (
          <article key={o.id} className="akatemia__osio">
            {/* Numero on rakennetta: kurssin osiot luetaan järjestyksessä,
                ja lukija haluaa tietää monesko tämä on. */}
            <span className="akatemia__nro">{String(i + 1).padStart(2, "0")}</span>
            <h3>{o.nimi}</h3>
            {o.video_url && (
              <p><a className="linkki" href={o.video_url} target="_blank" rel="noreferrer">
                {t("aca.katso_video")} ↗
              </a></p>
            )}
            {o.sisalto && <div className="akatemia__teksti">{o.sisalto}</div>}
          </article>
        ))}
      </div>

      {!ladataan && osiot.length === 0 && (
        <p className="dim">{t("aca.ei_sisaltoa")}</p>
      )}
    </section>
  );
}

/* ── Koe ───────────────────────────────────────────────────── */

function Koe({
  id, valmis, takaisin,
}: { id: number; valmis: () => Promise<void>; takaisin: () => void }) {
  const [kysymykset, setKysymykset] = useState<KoeKysymys[]>([]);
  const [raja, setRaja] = useState(80);
  const [valinnat, setValinnat] = useState<Record<number, number[]>>({});
  const [tulos, setTulos] = useState<KoeTulos | null>(null);
  const [lahetetaan, setLahetetaan] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);

  useEffect(() => {
    api.academyKoe(id)
      .then((r) => { setKysymykset(r.kysymykset); setRaja(r.lapaisyraja); })
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.koe_virhe")));
  }, [id]);

  function valitse(kysymys: KoeKysymys, vaihtoehtoId: number) {
    setValinnat((v) => {
      const nyt = v[kysymys.id] ?? [];
      if (kysymys.tyyppi === "yksi") return { ...v, [kysymys.id]: [vaihtoehtoId] };
      return {
        ...v,
        [kysymys.id]: nyt.includes(vaihtoehtoId)
          ? nyt.filter((x) => x !== vaihtoehtoId)
          : [...nyt, vaihtoehtoId],
      };
    });
  }

  async function laheta() {
    setLahetetaan(true); setVirhe(null);
    try {
      setTulos(await api.academyPalautaKoe(id,
        kysymykset.map((k) => ({ kysymys_id: k.id, vaihtoehdot: valinnat[k.id] ?? [] }))));
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.palautus_virhe"));
    } finally { setLahetetaan(false); }
  }

  const vastattu = kysymykset.filter((k) => (valinnat[k.id] ?? []).length > 0).length;

  if (tulos) {
    return (
      <section className="panel">
        <div className="panel__head">
          <h2>{tulos.lapaisty ? t("aca.lapaisty") : t("aca.ei_lapaisty")}</h2>
          <button className="nappi nappi--paa" onClick={() => void valmis()}>
            {t("aca.kursseihin")}
          </button>
        </div>
        <div className={"akatemia__tulos" + (tulos.lapaisty ? "" : " akatemia__tulos--hylatty")}>
          {/* Hylätystä ei näytetä pisteitä eikä oikeiden määrää (25.9.2026): rajattomilla yrityksillä ne
              paljastaisivat oikeat vastaukset kokeilemalla. */}
          {tulos.pisteet != null && <p className="akatemia__pisteet">{tulos.pisteet} %</p>}
          <p className="dim" style={{ margin: "0 0 14px" }}>
            {t("aca.tulos_meta", { n: tulos.kysymyksia, raja: tulos.lapaisyraja })}
          </p>
        <p className="dim" style={{ margin: 0 }}>
          {tulos.lapaisty
            ? t("aca.teoria_suoritettu")
            : t("aca.yritys_kirjattiin", { n: tulos.yritys })}
        </p>
        </div>
      </section>
    );
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("aca.koe")}</h2>
        <div>
          <button className="nappi nappi--kevyt" onClick={takaisin}>← {t("aca.materiaaliin")}</button>{" "}
          <button className="nappi nappi--paa"
                  disabled={lahetetaan || vastattu < kysymykset.length}
                  onClick={() => void laheta()}>
            {lahetetaan ? t("aca.lahetetaan") : t("aca.palauta", { n: vastattu, m: kysymykset.length })}
          </button>
        </div>
      </div>

      <p className="ikkuna__vihje">
        {t("aca.koe_vihje", { raja })}
      </p>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {kysymykset.map((k, i) => (
        <fieldset key={k.id}
                  className={"akatemia__kysymys"
                    + ((valinnat[k.id] ?? []).length > 0 ? " akatemia__kysymys--vastattu" : "")}>
          <legend>
            <span className="akatemia__nro">{String(i + 1).padStart(2, "0")}</span>{" "}
            {k.kysymys}
            {k.tyyppi === "monta" && <span className="dim"> — {t("aca.useampi_oikein")}</span>}
          </legend>
          <div className="akatemia__vaihtoehdot">
            {k.vaihtoehdot.map((v) => {
              const valittu = (valinnat[k.id] ?? []).includes(v.id);
              return (
                <label key={v.id} className={"valinta" + (valittu ? " valinta--on" : "")}>
                  <input type={k.tyyppi === "yksi" ? "radio" : "checkbox"}
                         name={`k${k.id}`} checked={valittu}
                         onChange={() => valitse(k, v.id)} />
                  {v.teksti}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </section>
  );
}

/* ── Sisällön hallinta ─────────────────────────────────────── */

function Hallinta({ onMuutos }: { onMuutos: () => Promise<void> }) {
  const [kurssit, setKurssit] = useState<AcademyOmaKurssi[]>([]);
  const [tyossa, setTyossa] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setKurssit(await api.academyKurssit() as AcademyOmaKurssi[]); } catch { /* ohitetaan */ }
  }, []);
  useEffect(() => { void lataa(); }, [lataa]);

  async function aja(teko: () => Promise<unknown>, viesti?: string) {
    setTyossa(true); setIlmoitus(null);
    try {
      await teko();
      if (viesti) setIlmoitus(viesti);
      await lataa(); await onMuutos();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("aca.toiminto_virhe"));
    } finally { setTyossa(false); }
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("aca.kurssit")}</h2>
        <button className="nappi nappi--kevyt" disabled={tyossa}
                onClick={() => void aja(async () => {
                  const r = await api.academyMallisisalto();
                  setIlmoitus(t("aca.luotu", { k: r.kursseja, o: r.osioita, n: r.kysymyksia }));
                })}>
          {t("aca.luo_esimerkki")}
        </button>
      </div>

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

      {kurssit.length === 0 ? (
        <p className="dim">
          {t("aca.ei_kursseja")}
        </p>
      ) : (
        <div className="taulukko-kaari">
          <table className="tbl">
            <thead>
              <tr>
                <th className="th--vasen">{t("aca.kurssi")}</th>
                <th className="th--vasen">{t("aca.rata")}</th>
                <th>{t("aca.osioita")}</th>
                <th>{t("aca.kysymyksia")}</th>
                <th className="th--vasen">{t("nippu.tila")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {kurssit.map((k) => (
                <tr key={k.id}>
                  <td>{k.nimi}</td>
                  <td>{k.rata ?? <span className="dim">{t("aca.yleinen")}</span>}</td>
                  <td className="mono">{k.osioita ?? 0}</td>
                  <td className="mono">{k.kysymyksia ?? 0}</td>
                  <td>{k.julkaistu
                    ? <b>{t("aca.julkaistu")}</b>
                    : <span className="dim">{t("aca.luonnos")}</span>}</td>
                  <td>
                    <button className="nappi nappi--kevyt" disabled={tyossa}
                            onClick={() => void aja(
                              () => api.academyJulkaise(k.id, !k.julkaistu),
                              k.julkaistu ? t("aca.poistettu_julkaisusta") : t("aca.julkaistu_ilmoitus"))}>
                      {k.julkaistu ? t("aca.poista_julkaisusta") : t("aca.julkaise")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="ikkuna__vihje">
        {t("aca.julkaisu_vihje")}
      </p>
    </section>
  );
}
